import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { CensusDisplay } from "@/components/staffingBlock/CensusDisplay";
import { useRevealWage } from "@/hooks/useRevealWage";
import { ChangesSource, type DayStats } from "@/utils/models";
import { formatCurrency, formatNumber } from "@/utils/utilFunctions";
import type { GapsDayHppd } from "../useGapsDayModel";
import { GapsMetricCell } from "./GapsMetricCell";
import { METRIC_VALUE_SX } from "./gapsMetricsStyles";

type Props = {
	dayStats: DayStats;
	dayHppd: GapsDayHppd | undefined;
	date: DateTime;
};

/** The day's stat cells: cost and PPD (wages only), HPPD against target, and the census editor. */
export const GapsDayStatCells: FC<Props> = ({ dayStats, dayHppd, date }) => {
	const { t } = useTranslation();
	const revealWage = useRevealWage();

	return (
		<>
			{/* Money is dropped entirely, not masked: a redacted number costs the same space and says
			    nothing (the artifact's own note). What remains is the clinical half of the drawer. */}
			{revealWage && (
				<>
					<GapsMetricCell label={t("gaps.metrics.totalCost")} dataTestId="gaps-metrics-total-cost">
						{formatCurrency(dayStats.totalSpent)}
					</GapsMetricCell>
					<GapsMetricCell label={t("gaps.metrics.ppd")} dataTestId="gaps-metrics-ppd">
						{formatCurrency(dayStats.ppd)}
					</GapsMetricCell>
				</>
			)}
			{/* Only HPPD waits for the saved settings (see `useGapsHomeModel`); cost, PPD and census do not depend on them.
			    With no target for the weekday (0) the desktop prints "2.94/0"; the phone names it "Not set" instead
			    (APP-886: a bare HPPD in the "HPPD / target" cell read as the target going missing), since a zero there is a
			    missing setting, not a goal of zero hours. */}
			{dayHppd && (
				<GapsMetricCell label={t("gaps.metrics.hppdTitle")} dataTestId="gaps-metrics-hppd-target">
					{dayHppd.target > 0
						? `${formatNumber(dayHppd.hppd)} / ${formatNumber(dayHppd.target)}`
						: t("gaps.metrics.hppdTargetNotSet", { hppd: formatNumber(dayHppd.hppd) })}
				</GapsMetricCell>
			)}
			{/* The real census editor, not a link to one — the same control the staffing block uses, so an
			    edit here writes through exactly the path an edit there does. */}
			<GapsMetricCell label={t("gaps.metrics.census")} dataTestId="gaps-metrics-census">
				<CensusDisplay source={ChangesSource.Staffing} dayDate={date} censusValue={dayStats.census} showBedIcon={false} censusSx={METRIC_VALUE_SX} />
			</GapsMetricCell>
		</>
	);
};
